<template>
  <div class="OrderTable">
    <datatable
      :columns="columns"
      :data="data"
      :total="total"
      :query="query"
      :xprops="xprops"
      :selection="value"
      :Pagination="false"
      :HeaderSettings="false"
    >
      <div
        style="
          display: flex;
          justify-content: space-between;
          align-items: center;
        "
      >
        <div flex-grow="1">
          <div width="100%" height="100%">
            <input
              class="filter ui-icon-search"
              type="search"
              v-model="search"
              placeholder="Search orders"
            />
            <input
              type="submit"
              disabled="true"
              class="filter ui-icon-search-btn"
              value=""
            />
            <div>
              <slot name="searchHelpText"></slot>
            </div>
          </div>
        </div>
        <div v-if="colorByType" class="OrderTable__legend">
          <span class="OrderTable__legend-label">Type:</span>
          <span class="OrderTable__legend-item">
            <span
              class="OrderTable__legend-swatch OrderTable__legend-swatch--standard"
            ></span>
            Delivery
          </span>
          <span class="OrderTable__legend-item">
            <span
              class="OrderTable__legend-swatch OrderTable__legend-swatch--xdock"
            ></span>
            Crossdock
          </span>
        </div>
        <div style="margin-right: 10px">
          <slot></slot>
        </div>
      </div>
    </datatable>
  </div>
</template>
<script>
import Vue from 'vue';
import orderBy from 'lodash.orderby';
import filterBy from 'lodash.filter';
import * as components from './components';

const sumBy = (arr = [], fn) => arr.reduce((sum, el) => sum + fn(el), 0);

// SD-581: Group order rows into color categories for the Pending tab.
// Returns 'xdock' | 'standard'. Pickups/returns roll into standard.
function colorCategoryFor(row) {
  const type = (row.type || '').toLowerCase();
  const orderType = (row.orderType || '').toLowerCase();
  if (type.includes('crossdock') || orderType.includes('crossdock'))
    return 'xdock';
  return 'standard';
}

// SD-581: alternate light/dark by overall row position; the color hue is
// picked from the row's category. Every other row is dark regardless of type.
function rowColorClasses(orders) {
  return orders.map((order, i) => {
    const cat = colorCategoryFor(order);
    const isDark = i % 2 === 1;
    return `row-${cat}-${isDark ? 'dark' : 'light'}`;
  });
}

export default {
  components,
  props: [
    'orders',
    'cols',
    'value',
    'getRowButtonText',
    'getRowButtonVisible',
    'getRowRolloverButtonVisible',
    'colProps',
    'tab',
    'colorByType',
  ],
  data() {
    const allColumns = [
      {
        title: 'Order #',
        field: 'skOrderNum',
        tdComp: 'tdOrderNum',
      },
      {
        title: 'Wave',
        field: 'wave',
        tdComp: 'tdWave',
      },
      {
        title: 'Truck',
        field: 'truck',
        tdComp: 'tdTruck',
        thComp: 'thSortable',
      },
      { title: 'Dist', field: 'routedBy', tdComp: 'tdDistribution' },
      // { title: 'Pick Status', field: 'pickStatus', tdComp: 'tdPickStatus' },
      {
        title: 'Zone',
        field: 'zone',
        tdComp: 'tdZone',
        thComp: 'thZoneFilter',
      },
      { title: 'Account', tdComp: 'tdAccount' },
      {
        title: 'Type',
        field: 'type',
        tdComp: 'tdType',
        thComp: 'thTypeFilter',
      },
      { title: 'Order Notes', field: 'orderNotes', tdComp: 'tdOrderNotes' },
      {
        title: 'Delivery Notes',
        field: 'deliveryNotes',
        tdComp: 'tdDeliveryNotes',
      },
      { title: 'Address', field: 'address', tdComp: 'tdAddress' },
      { title: 'Qty', field: 'qty', tdComp: 'tdQty' },
      { title: 'Qty Req', field: 'boxesRequested', tdComp: 'tdQtyRequested' },
      { title: 'Qty Scnd', field: 'boxesScanned', tdComp: 'tdQtyScanned' },
      { title: 'Invoices', field: 'invoices', tdComp: 'tdInvoices' },
      { title: 'Status', field: 'status', tdComp: 'tdStatus' },
      { title: 'Action', field: 'id', tdComp: 'tdButton' },
    ];

    const columns = allColumns.filter((column) => {
      return this.cols.find((col) => {
        return column.title === (typeof col === 'string' ? col : col.title);
      });
    });

    return {
      columns,
      search: '',
      query: {
        // limit: 25,
        // offset: 0,
        sort: 'truck',
        order: 'asc',
      },
      xprops: {
        getRowButtonText: this.getRowButtonText,
        getRowButtonVisible: this.getRowButtonVisible,
        getRowRolloverButtonVisible: this.getRowRolloverButtonVisible,
        getColProps: this.getColProps,
        eventbus: new Vue(), // only for the current Datatable instance
        tab: this.tab,
      },
    };
  },
  created() {
    this.xprops.eventbus.$on('TD_BUTTON_CLICK', (row) => {
      this.$emit('onRowButtonClick', row);
    });
    this.xprops.eventbus.$on('TD_ROLLOVER_BUTTON_CLICK', (row) => {
      this.$emit('onRowRolloverButtonClick', row);
    });
  },
  methods: {
    getColProps(title, row) {
      const col = this.colPropsByTitle[title];

      if (!col) {
        return {};
      }

      if (typeof col.props === 'function') {
        return col.props(row);
      }

      return col.props;
    },
    applyRowColors() {
      // SD-581: Tag each <tr> with its color class. vue2-datatable doesn't
      // expose a per-row class hook, so we sync to the rendered DOM after
      // every data change. Only the Pending tab opts in via :color-by-type.
      if (!this.colorByType) return;
      const tbody = this.$el && this.$el.querySelector('.table tbody');
      if (!tbody) return;
      const rows = tbody.querySelectorAll('tr');
      const classes = rowColorClasses(this.sorted);
      rows.forEach((tr, i) => {
        tr.classList.remove(
          'row-xdock-light',
          'row-xdock-dark',
          'row-standard-light',
          'row-standard-dark'
        );
        if (classes[i]) tr.classList.add(classes[i]);
      });
    },
  },
  computed: {
    colPropsByTitle() {
      return Object.fromEntries(
        this.cols
          .filter((col) => typeof col === 'object' && col.title)
          .map((col) => [col.title, col])
      );
    },
    filtered() {
      let orders;

      // First, filter by header dropdowns
      const filters = {};
      const { zone, type } = this.query;

      if (zone && zone !== 'All') {
        filters.zone = zone;
      }

      if (type && type !== 'All' && !type.includes('Common_Carrier')) {
        filters.type = type.toLowerCase();
      }

      orders = filterBy(this.orders, filters);

      // Extra filter step for Common Carrier options that cannot be handled by filterBy
      // the values passed to .includes (here and above) and .split are dependent on option label in thTypeFilter.vue
      if (type && type.includes('Common_Carrier')) {
        let carrier = type.split('_-_')[1];
        orders = filterBy(orders, (order) => {
          // Non-common-carrier orders have requestedCommonCarrier === null in
          // prod; .includes() on null would throw and freeze the dropdown.
          const rcc = order.requestedCommonCarrier || '';
          if (rcc.includes(carrier)) return true;
          return (order.trackers || []).some((tracker) =>
            (tracker.carrier || '').includes(carrier)
          );
        });
      }

      // Next, filter by search bar text
      if (this.search) {
        const search = this.search.toLowerCase();
        const searchable = [
          'skOrderNum',
          'account',
          'zone',
          'region',
          'status',
          'address',
          'orderType',
          'category',
          'cod',
        ];
        orders = orders.filter((order) =>
          searchable.some(
            (field) =>
              order[field] && order[field].toLowerCase().includes(search)
          )
        );
      }

      return orders;
    },
    sorted() {
      // Skip sorting if a sortable column is not included
      if (!this.columns.find((c) => c.thComp === 'thSortable')) {
        return this.filtered;
      }

      const { sort, order } = this.query;

      if (sort === 'truck') {
        return orderBy(this.filtered, ['truck', 'stop'], [order, 'asc']);
      }

      return orderBy(this.filtered, [sort], [order]);
    },
    // paginated() {
    //   const { limit, offset } = this.query;
    //   return this.sorted.slice(offset, offset + limit);
    // },
    data() {
      // return this.paginated.slice();
      return this.sorted.slice();
    },
    total() {
      return this.filtered.length;
    },
  },
  watch: {
    value(selection) {
      this.$emit('input', selection);
    },
    sorted: {
      immediate: true,
      handler() {
        this.$nextTick(this.applyRowColors);
      },
    },
  },
};
</script>

<style>
.OrderTable .table thead > tr > th,
.OrderTable .table tbody > tr > th,
.OrderTable .table tfoot > tr > th,
.OrderTable .table thead > tr > td,
.OrderTable .table tbody > tr > td,
.OrderTable .table tfoot > tr > td {
  vertical-align: middle;
}

/* SD-581: Color-code rows by order type. Override .table-striped's
   nth-child alternation so our type-based pattern wins. */
.OrderTable .table > tbody > tr.row-xdock-light > td,
.OrderTable .table > tbody > tr.row-xdock-dark > td,
.OrderTable .table > tbody > tr.row-standard-light > td,
.OrderTable .table > tbody > tr.row-standard-dark > td {
  background-color: var(--row-bg) !important;
}
.OrderTable .table > tbody > tr.row-xdock-light > td:first-child,
.OrderTable .table > tbody > tr.row-xdock-dark > td:first-child,
.OrderTable .table > tbody > tr.row-standard-light > td:first-child,
.OrderTable .table > tbody > tr.row-standard-dark > td:first-child {
  box-shadow: inset 6px 0 0 0 var(--row-accent);
  /* Hide Bootstrap's 1px border-top on the first cell so the colored accent
     bar reads as one continuous strip down the side. Row separators stay on
     the other cells. */
  border-top-color: transparent;
}
.OrderTable .table > tbody > tr.row-xdock-light {
  --row-bg: #fffaea;
  --row-accent: #f0a800;
}
.OrderTable .table > tbody > tr.row-xdock-dark {
  --row-bg: #fff0c7;
  --row-accent: #f0a800;
}
.OrderTable .table > tbody > tr.row-standard-light {
  --row-bg: #f3f8fd;
  --row-accent: #2c7be5;
}
.OrderTable .table > tbody > tr.row-standard-dark {
  --row-bg: #dce9f6;
  --row-accent: #2c7be5;
}

/* SD-581: Legend bar */
.OrderTable__legend {
  display: inline-flex;
  align-items: center;
  gap: 18px;
  font-size: 12px;
  color: #444;
  padding: 6px 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: #fafafa;
  margin: 0 12px;
}
.OrderTable__legend-label {
  font-weight: 600;
  color: #666;
}
.OrderTable__legend-item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.OrderTable__legend-swatch {
  display: inline-block;
  width: 33px;
  height: 14px;
  border: 1px solid #999;
  background-image: linear-gradient(
    to right,
    var(--accent) 0,
    var(--accent) 5px,
    var(--light) 5px,
    var(--light) 19px,
    var(--dark) 19px,
    var(--dark) 33px
  );
}
.OrderTable__legend-swatch--standard {
  --accent: #2c7be5;
  --light: #f3f8fd;
  --dark: #dce9f6;
}
.OrderTable__legend-swatch--xdock {
  --accent: #f0a800;
  --light: #fffaea;
  --dark: #fff0c7;
}
</style>

<!-- disable top refresh button on shorts -->
<!-- only show short -->
<!-- consider cases as boxes -->
<!-- show warning when match doesn't match -->



// WEBPACK FOOTER //
// src/components/OrderTable/index.vue